import { AiIcon } from '@plato/ui-legacy/assets/icons/ai-icon';
import { ProductIcon } from '@plato/ui-legacy/assets/icons/product-icon';
import { Button } from '@plato/ui-legacy/components/ui/button';
import Text from '@plato/ui-legacy/components/ui/text';
import { cn } from '@plato/ui/util/classes';
import * as Collapsible from '@radix-ui/react-collapsible';
import { keepPreviousData } from '@tanstack/react-query';
import { ChevronDownIcon, PlusIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PulseLoader } from 'react-spinners';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import type { SalesDraftOrder } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { ProductLinkCell } from '../../list-views/components/product-link-cell.tsx';

export type RecommendedProductsProps = {
    draft: SalesDraftOrder;
    onSelectProduct: (product: ProductVariantDTO) => void;
};

export function RecommendedProducts({ draft, onSelectProduct }: RecommendedProductsProps): ReactElement {
    const { t } = useTranslation('sales-draft');

    const { data, isLoading } =
        api.platoUser.query.salesDraft.getInventory.getTopProductsForCustomer.useQuery(
            {
                customer_id: draft.customer_id,
                exclude_product_variant_ids: draft.line_items
                    .map((item) => item.normal_line_item?.product_variant_id)
                    .filter((x): x is string => !!x),
            },
            {
                placeholderData: keepPreviousData,
            },
        );

    return (
        <Collapsible.Root
            defaultOpen
            className={cn('bg-neutral-25 flex shrink-0 flex-col gap-3 rounded-lg p-3.5', isLoading && 'grow')}
        >
            <Collapsible.Trigger className="group flex items-center justify-between gap-2">
                <span className="flex gap-2 items-center">
                    <AiIcon />
                    <Text weight="semibold" color="neutral-800">
                        {data?.source === 'customer'
                            ? t('sales_draft__recommended_header__customer')
                            : data?.source === 'tenant'
                              ? t('sales_draft__recommended_header__tenant')
                              : t('sales_draft__recommended_header')}
                    </Text>
                </span>
                <div className="flex items-center px-3">
                    <ChevronDownIcon className="h-4 w-4 text-neutral-500 transition-transform group-data-[state=closed]:-rotate-90" />
                </div>
            </Collapsible.Trigger>
            <Collapsible.Content className="flex flex-col">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-6">
                        <div className="flex items-baseline">
                            <Text variant="ai">{t('sales_draft__recommended_loading')}</Text>
                            <PulseLoader size={3} color="#8D54E9" />
                        </div>
                    </div>
                ) : data?.items.length ? (
                    <div className="flex flex-col gap-3">
                        {data.items.map((product) => (
                            <div
                                key={product.product_variant_id}
                                className="border-b py-2 text-sm text-neutral-700 flex gap-2 items-center last:border-0"
                            >
                                <ProductIcon className="text-neutral-400 size-4" />
                                <ProductLinkCell
                                    erpProductId={product.product_variant_id}
                                    productName={product.product_variant_name}
                                    className="grow min-w-0"
                                    topClassName="text-blue-400"
                                    bottomClassName="text-neutral-600 line-clamp-2"
                                />
                                <div className="shrink-0">
                                    <Button
                                        variant="icon"
                                        className="w-10 h-10"
                                        icon={PlusIcon}
                                        onClick={() => {
                                            onSelectProduct(product);
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-3 mt-5">
                        <Text className="text-center" color="neutral-600">
                            {t('sales_draft__recommended_none')}
                        </Text>
                    </div>
                )}
            </Collapsible.Content>
        </Collapsible.Root>
    );
}
